import { useAppDispatch, useAppSelector } from "@/app/reduxHooks";
import { getRegisteredTables } from "@/features/adp/services/api-service";
import {
  closeOverlay,
  openOverlay,
  type RegisteredDestinationsOverlayData,
} from "@/features/adp/state/overlaySlice";
import { Button } from "@/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/dialog";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft } from "lucide-react";
import styles from "./RegisteredDestinationsDetails.module.scss";

function isRegisteredDestinationsOverlayData(
  value: unknown
): value is RegisteredDestinationsOverlayData {
  return Boolean(value && typeof value === "object");
}

const RegisteredDestinationsDetails = () => {
  const dispatch = useAppDispatch();
  const { isOpen, type, data } = useAppSelector((state) => state.overlay);

  const isVisible = isOpen && type === "registered-destinations-details";
  const modalData = isVisible && isRegisteredDestinationsOverlayData(data) ? data : null;
  const targetDataSourceId = modalData?.targetDataSourceId ?? null;

  const { data: registeredTablesResponse } = useQuery({
    queryKey: ["registered-destination-details", targetDataSourceId],
    queryFn: () => getRegisteredTables(targetDataSourceId ?? 0),
    enabled: isVisible && typeof targetDataSourceId === "number" && targetDataSourceId > 0,
  });

  const registeredDatabase = registeredTablesResponse?.data?.databases?.[0];
  const destinationName =
    modalData?.destinationName ??
    registeredDatabase?.source_name ??
    "-";
  const databaseName =
    modalData?.databaseName ??
    registeredDatabase?.database ??
    "-";
  const schemaName =
    modalData?.schemaName ??
    registeredDatabase?.schema ??
    "-";

  const handleClose = () => dispatch(closeOverlay());

  const handleNext = () => {
    if (!modalData) return;
    dispatch(openOverlay({
      type: "registered-destinations-table-list",
      data: {
        ...modalData,
        destinationName,
        databaseName,
        schemaName,
      },
    }));
  };

  return (
    <Dialog
      open={isVisible}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
    >
      <DialogContent
        className="max-w-5xl"
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <DialogHeader className={styles.header}>
          <div className={styles.breadcrumb}>
            <Button
              type="button"
              variant="text"
              className={styles.backButton}
              onClick={handleClose}
            >
              <ChevronLeft className="h-4 w-4" />
              Back
            </Button>
            <span className={styles.breadcrumbMuted}>Destination Details</span>
            <span className={styles.breadcrumbSeparator}>&gt;</span>
            <span className={styles.breadcrumbCurrent}>Config Details</span>
          </div>
        </DialogHeader>

        <DialogBody className={styles.body}>
          <section className={styles.configPanel}>
            <DialogTitle className={styles.title}>Configuration Details</DialogTitle>

            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>Destination Name</label>
              <div className={styles.readOnlyField}>{destinationName}</div>
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>Database Name</label>
              <div className={styles.readOnlyField}>{databaseName}</div>
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>Schema Name</label>
              <div className={styles.readOnlyField}>{schemaName}</div>
            </div>
          </section>
        </DialogBody>

        <DialogFooter className={styles.footer}>
          <Button type="button" className={styles.nextButton} onClick={handleNext}>
            Next
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RegisteredDestinationsDetails;
